/**
 * API ISTEMCISI
 *
 * Sozlesme: /opt/email-panelim/sozlesme/openapi.json
 * Kurallar:
 *  - Jeton localStorage'da; 401 gelince oturum temizlenir ve girise donulur.
 *  - Yazma isteklerinde islem-anahtari gonderilir: ag koptugunda ayni anahtarla
 *    yeniden denemek GUVENLIDIR, islem iki kez uygulanmaz.
 *  - Hata govdesi her zaman { hata, mesaj, ayrinti?, istekId }.
 */

// API her zaman ayni kok: uretimde nginx, gelistirmede vite proxy yonlendirir.
const KOK = "/api";
const JETON_ANAHTARI = "panelim.jeton";
const KULLANICI_ANAHTARI = "panelim.kullanici";

export type Rol = "operator" | "musteri" | "kutu";

export interface Kullanici {
  eposta: string;
  ad: string | null;
  rol: Rol;
  kiraciId: string | null;
}

export class ApiHatasi extends Error {
  constructor(
    override readonly message: string,
    readonly kod: string,
    readonly durum: number,
    readonly istekId?: string,
    readonly ayrinti?: unknown,
  ) {
    super(message);
  }
}

// --------------------------------------------------------------- oturum

export const oturum = {
  jeton(): string | null {
    try {
      return localStorage.getItem(JETON_ANAHTARI);
    } catch {
      return null;
    }
  },
  kullanici(): Kullanici | null {
    try {
      const h = localStorage.getItem(KULLANICI_ANAHTARI);
      return h ? (JSON.parse(h) as Kullanici) : null;
    } catch {
      return null;
    }
  },
  yaz(jeton: string, k: Kullanici) {
    try {
      localStorage.setItem(JETON_ANAHTARI, jeton);
      localStorage.setItem(KULLANICI_ANAHTARI, JSON.stringify(k));
    } catch {
      /* gizli sekme: bellekte kalir, sayfa yenilenince cikis olur */
    }
  },
  temizle() {
    try {
      localStorage.removeItem(JETON_ANAHTARI);
      localStorage.removeItem(KULLANICI_ANAHTARI);
    } catch {
      /* yoksay */
    }
  },
};

/**
 * VEKÂLETEN ÇALIŞMA (yalnızca operatör)
 *
 * Operator bir musterinin gozuyle bakarken bu deger doludur ve HER
 * istege `Vekaleten` basligi olarak eklenir. Sunucuda "su an vekaleten
 * calisiyor" diye tutulan bir durum yoktur; her istek kendi niyetini
 * kendisi tasir.
 *
 * sessionStorage kullaniliyor, localStorage DEGIL: vekalet sekmeyi
 * kapatinca biter. Tarayiciyi kapatip acan biri kendi kimligiyle geri
 * gelmeli — baskasinin sapkasini takmis olarak degil.
 */
const VEKALET_ANAHTARI = "panelim.vekalet";

export interface VekaletDurumu {
  kiraciId: string;
  ad: string;
}

export const vekalet = {
  simdiki(): VekaletDurumu | null {
    try {
      const h = sessionStorage.getItem(VEKALET_ANAHTARI);
      return h ? (JSON.parse(h) as VekaletDurumu) : null;
    } catch {
      return null;
    }
  },
  basla(kiraciId: string, ad: string) {
    try {
      sessionStorage.setItem(VEKALET_ANAHTARI, JSON.stringify({ kiraciId, ad }));
    } catch {
      /* gizli sekme */
    }
  },
  bitir() {
    try {
      sessionStorage.removeItem(VEKALET_ANAHTARI);
    } catch {
      /* yoksay */
    }
  },
};

type Yontem = "GET" | "POST" | "PATCH" | "DELETE";

let oturumBittiKanca: (() => void) | null = null;
export function oturumBitince(f: () => void) {
  oturumBittiKanca = f;
}

async function istek<T>(yontem: Yontem, yol: string, govde?: unknown, islemAnahtari?: string): Promise<T> {
  const basliklar: Record<string, string> = {};
  const j = oturum.jeton();
  if (j) basliklar.Authorization = "Bearer " + j;
  // Vekaleten calisiliyorsa sunucu yetkiyi o kiraciya DARALTIR
  const vk = vekalet.simdiki();
  if (vk) basliklar["Vekaleten"] = vk.kiraciId;
  if (govde !== undefined) basliklar["Content-Type"] = "application/json";
  if (yontem !== "GET") basliklar["islem-anahtari"] = islemAnahtari ?? crypto.randomUUID();

  let yanit: Response;
  try {
    yanit = await fetch(KOK + yol, {
      method: yontem,
      headers: basliklar,
      body: govde === undefined ? undefined : JSON.stringify(govde),
    });
  } catch {
    throw new ApiHatasi("Sunucuya ulasilamadi. Baglantinizi kontrol edin.", "ag", 0);
  }

  if (yanit.status === 204) return undefined as T;

  let veri: any = null;
  try {
    veri = await yanit.json();
  } catch {
    /* govdesiz yanit */
  }

  if (!yanit.ok) {
    if (yanit.status === 401) {
      oturum.temizle();
      oturumBittiKanca?.();
    }
    throw new ApiHatasi(
      veri?.mesaj ?? "Beklenmeyen bir hata olustu",
      veri?.hata ?? "bilinmiyor",
      yanit.status,
      veri?.istekId,
      veri?.ayrinti,
    );
  }
  return veri as T;
}

// --------------------------------------------------------------- tipler

export interface AlanAdi {
  id: string;
  name: string;
  description?: string | null;
  memberTenantId?: string | null;
  createdAt?: string;
}

export interface Kutu {
  id: string;
  name: string;
  emailAddress: string;
  description?: string | null;
  domainId?: string;
  memberTenantId?: string | null;
  usedDiskQuota?: number;
  panelDurum?: string;
  /** Yöneticinin koyduğu disk hedefi (MB). null = hedef yok */
  kotaHedefMb?: number | null;
}

export interface KutuAyrinti extends Kutu {
  sebep?: string | null;
  takmaAdlar?: string[];
  kaliciSilme?: string | null;
}

export interface SilmeKaydi {
  hesap_id: string;
  eposta: string;
  kiraci_id: string;
  silen: string;
  silme_zamani: string;
  kalici_silme: string;
  kalanGun: number;
}

export interface Musteri {
  id: string;
  ad: string;
  yetkili_ad: string | null;
  yetkili_eposta: string | null;
  telefon: string | null;
  paket: string;
  panel_alan: string | null;
  durum: string;
  olusturma: string;
}

export interface Paket {
  kutu: number | null;
  diskMb: number | null;
  gunlukAlici: number;
  alanAdi: number | null;
}

export interface LimitDurum {
  kiraciId: string;
  paketAdi: string;
  paket: Paket;
  kutu: { kullanilan: number; limit: number | null };
  disk: { kullanilanMb: number; limitMb: number | null };
  gonderim: { kullanilan: number; limit: number; kalan: number; kilitli: boolean };
  kilitliKutular: string[];
  gecmis: KullanimGun[];
}

export interface KullanimGun {
  tarih: string;
  kiraci_id: string;
  kutu_sayisi: number;
  disk_bayt: number;
  giden_alici: number;
  gelen_ileti: number;
}

export interface DnsKayit {
  tur: string;
  ad: string;
  beklenen: string;
  canli: string | null;
  durum: "tamam" | "eksik" | "hatali";
  zorunlu: boolean;
  aciklama: string;
  tespit?: string;
}

export interface AlanDnsDurum {
  alanAdi: string;
  hazir: boolean;
  gonderimAcik: boolean;
  otomatikAyarlanabilir: boolean;
  dnsBizde: boolean;
  kayitlar: DnsKayit[];
  eksikZorunlu: number;
}

export interface DnsYazmaSonuc {
  ad: string;
  tur: string;
  durum: "eklendi" | "guncellendi" | "degismedi" | "hata";
  mesaj?: string;
}

export interface KutumBilgi {
  veri: {
    eposta: string;
    aciklama: string | null;
    diskBayt: number;
    durum: string;
    sebep: string | null;
    takmaAdlar: string[];
    kurum: string | null;
    kurumGunlukGonderim: { kullanilan: number; limit: number };
  };
  ayarlar: {
    webmail: string;
    imap: { sunucu: string; port: number; guvenlik: string };
    smtp: { sunucu: string; port: number; guvenlik: string };
    kullaniciAdi: string;
  };
}

export interface DikkatMaddesi {
  tur: string;
  agirlik: "bilgi" | "uyari" | "kritik";
  baslik: string;
  aciklama: string;
  adet: number;
  yol: string;
}

export interface Kapasite {
  kiraciId: string;
  ad: string;
  paket: string;
  durum: string;
  kutu: { kullanilan: number; limit: number | null; oran: number };
  disk: { kullanilanBayt: number; kullanilanMb: number; limitMb: number | null; oran: number };
  gonderim: { kullanilan: number; limit: number; oran: number };
  alanAdi: { kullanilan: number; limit: number | null; oran: number };
  kilitliKutu: number;
}

export interface TrafikGun {
  tarih: string;
  giden: number;
  gelen: number;
}

export interface PanoOperator {
  rol: "operator";
  kullanici: string;
  zaman: string;
  dikkat: DikkatMaddesi[];
  saglik: {
    yedek: { dosya: string | null; zaman: string | null; saatOnce: number | null };
    disk: { toplam: number; kullanilan: number; bos: number; oran: number } | null;
    alanAdi: number;
    dnsHazir: number;
  };
  gostergeler: {
    musteri: number;
    aktifMusteri: number;
    alanAdi: number;
    kutu: number;
    kilitliKutu: number;
    toplamDiskBayt: number;
    bugunGiden: number;
    bugunGelen: number;
    dunGiden: number;
    dunGelen: number;
  };
  kapasite: Kapasite[];
  trafik: TrafikGun[];
  enBuyukKutular: Array<{ eposta: string; bayt: number; kiraci: string; kilitli: boolean }>;
  enCokGonderen: Array<{ eposta: string; giden_alici: number; giden_ileti: number }>;
  sonIslemler: Array<{ eylem: string; kullanici: string; rol: string; durum: string; baslangic: string }>;
}

export interface PanoMusteri {
  rol: "musteri";
  kullanici: string;
  kurum: string | null;
  paket: string;
  zaman: string;
  dikkat: DikkatMaddesi[];
  kapasite: Kapasite | undefined;
  kutular: Array<{ id: string; eposta: string; bayt: number; kilitli: boolean; durum: string }>;
  alanAdlari: Array<{ id: string; ad: string; hazir: boolean; sonKontrol: string | null }>;
  trafik: TrafikGun[];
  silmeBekleyen: Array<{ eposta: string; kaliciSilme: string; kalanGun: number }>;
}

export interface GuvenlikOzet {
  /** Operatör mü — müşteride eşik ve bildirim bölümleri gizlenir */
  operator?: boolean;
  esikler: Record<string, number>;
  olaylar: Array<{
    id: number;
    tur: string;
    agirlik: string;
    eposta: string | null;
    aciklama: string;
    islem: string | null;
    zaman: string;
  }>;
  bugun: Array<{
    eposta: string;
    kiraci_id: string | null;
    giden_ileti: number;
    giden_alici: number;
    en_cok_alici: number;
    farkli_alan: number;
    basarisiz_giris: number;
    ip_sayisi: number;
  }>;
  bildirim: { telegram: boolean; asgari: string | null } | null;
}

export interface Sapma {
  tur: string;
  onem: "bilgi" | "uyari" | "kritik";
  nesneTip: string;
  nesneId: string;
  ad: string;
  aciklama: string;
  onarilabilir: boolean;
}

export interface MutabakatSonuc {
  zaman: string;
  sapmalar: Sapma[];
  ozet: { bilgi: number; uyari: number; kritik: number };
  incelenen: { kutu: number; alan: number; kiraci: number };
}

export interface Alarm {
  id: number;
  tur: string;
  kiraci_id: string | null;
  kullanici: string | null;
  ayrinti: string | null;
  goruldu: number;
  zaman: string;
}

export interface DenetimKaydi {
  id: number;
  islem_anahtar: string;
  kiraci_id: string | null;
  kullanici: string;
  rol: string;
  eylem: string;
  nesne_tip: string | null;
  nesne_id: string | null;
  durum: string;
  hata: string | null;
  baslangic: string;
  bitis: string | null;
}

export interface KilitliKutu {
  hesapId: string;
  eposta: string;
  kiraci: string;
  panelDurum: string;
  sebep: string | null;
}

export interface KiraciRapor {
  kiraci: string;
  ad: string;
  limit: number;
  kullanilan: number;
  kilitlenen: string[];
  acilan: string[];
  hata: string[];
}


// --------------------------------------------------------------- ticari

export interface Fiyat {
  paket: string;
  aylik_kurus: number;
  yillik_kurus: number;
  guncelleme: string;
  paketDetay: Paket | null;
  aylikMetin: string;
  yillikMetin: string;
}

export interface Abonelik {
  kiraci_id: string;
  paket: string;
  donem: "aylik" | "yillik";
  durum: "deneme" | "aktif" | "gecikmis" | "iptal";
  baslangic: string;
  deneme_bitis: string | null;
  sonraki_fatura: string;
  vade_gun: number;
  tolerans_gun: number;
  fatura_unvan: string | null;
  vergi_no: string | null;
  vergi_dairesi: string | null;
  fatura_adres: string | null;
  notlar: string | null;
  guncelleme: string;
}

export interface AbonelikSatir extends Abonelik {
  musteriAdi: string;
  musteriDurum: string;
  acikBakiye: number;
}

export interface FaturaSatir {
  id?: number;
  aciklama: string;
  adet: number;
  birim_kurus: number;
  tutar_kurus: number;
}

export interface Fatura {
  id: number;
  no: string;
  kiraci_id: string;
  donem_bas: string;
  donem_bit: string;
  ara_toplam_kurus: number;
  kdv_oran: number;
  kdv_kurus: number;
  toplam_kurus: number;
  durum: "bekliyor" | "odendi" | "gecikti" | "iptal";
  kesim: string;
  vade: string;
  odeme_zamani: string | null;
  odeme_yontemi: string | null;
  odeme_notu: string | null;
  unvan: string | null;
  vergi_no: string | null;
  vergi_dairesi: string | null;
  adres: string | null;
  otomatik: number;
  olusturma: string;
  musteriAdi: string;
  toplamMetin: string;
  gecikmeGun: number;
}

export interface FaturaDonguSonuc {
  incelenen: number;
  kesilen: string[];
  gecikenler: string[];
  askiyaAlinan: string[];
  denemeBiten: string[];
  hata: string[];
}


// --------------------------------------------------------------- belge indirme

/**
 * Kimlik isteyen bir belgeyi indirir.
 *
 * Duz `<a href="/api/...">` Authorization basligi GONDERMEZ; sunucu 401 doner
 * ve kullanici yeni sekmede JSON hatasi gorur. Bu yuzden belge jetonla
 * cekilir, blob'a alinir ve indirme oradan tetiklenir.
 */
export async function belgeIndir(yol: string, dosyaAdi: string): Promise<void> {
  const j = oturum.jeton();
  const yanit = await fetch(KOK + yol, {
    headers: j ? { Authorization: "Bearer " + j } : {},
  });
  if (!yanit.ok) {
    if (yanit.status === 401) {
      oturum.temizle();
      oturumBittiKanca?.();
    }
    let mesaj = "Belge indirilemedi";
    try {
      mesaj = (await yanit.json())?.mesaj ?? mesaj;
    } catch {
      /* govde JSON degil */
    }
    throw new ApiHatasi(mesaj, "belge", yanit.status);
  }
  const blob = await yanit.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = dosyaAdi;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Hemen iptal edilirse Safari indirmeyi yarida kesiyor
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}


export interface IstemciAyar {
  kurum: string | null;
  kullaniciAdi: string;
  imap: { sunucu: string; port: number; guvenlik: string };
  smtp: { sunucu: string; port: number; guvenlik: string };
  /** Takvim ve kisiler ayni parolayi kullanir; ayri hesap acilmaz. */
  caldav?: { sunucu: string; port: number; yol: string; guvenlik: string };
  carddav?: { sunucu: string; port: number; yol: string; guvenlik: string };
  /** Sunucu tarafi kural (filtre) protokolu */
  sieve?: { sunucu: string; port: number; guvenlik: string };
  webmail: string;
  otomatik: { autoconfig: string | null; autodiscover: string | null; appleProfil: string | null };
}


export interface DmarcKaynak {
  ip: string;
  ileti: number;
  gecen: number;
  kalan: number;
  bizimMi: boolean;
  spfAlan: string;
  dkimAlan: string;
}

export interface DmarcOzet {
  alan: string;
  gun: number;
  toplamIleti: number;
  gecen: number;
  kalan: number;
  oran: number;
  saglayicilar: string[];
  kaynaklar: DmarcKaynak[];
  supheli: DmarcKaynak[];
}

export interface KotaDurum {
  kiraciId: string;
  ad: string;
  paket: string;
  disk: { kullanilanBayt: number; kullanilanMb: number; limitMb: number | null; oran: number };
  kutu: { kullanilan: number; limit: number | null; oran: number };
  asilanEsik: number;
  enBuyukKutular: Array<{ eposta: string; bayt: number }>;
}

// --------------------------------------------------------------- uclar

export const api = {
  /** Bu adres kimin kapisi — kimlik istemez, panel acilirken sorar */
  konak: () =>
    istek<{
      veri: {
        kiraci: boolean;
        kiraciId: string | null;
        ad: string | null;
        renk: string | null;
        logo: string | null;
      };
    }>("GET", "/konak"),

  // ---- tek kullanimlik kurulum baglantisi (kimlik ISTEMEZ) ----

  /** Baglantinin durumunu sorar. Gecersizse SEBEP disinda bilgi vermez. */
  kurulumLinkiDurum: (jeton: string) =>
    istek<{
      veri: {
        durum: "gecerli" | "yok" | "suresi_doldu" | "kullanildi";
        eposta?: string;
        amac?: string;
        sonKullanma?: string;
        enAzParola?: number;
      };
    }>("GET", `/kurulum/${encodeURIComponent(jeton)}`),

  /** Parolayi kurar ve baglantiyi yakar. */
  kurulumParolaKur: (jeton: string, parola: string) =>
    istek<{ veri: { tamam: true; eposta: string; webmail: string } }>(
      "POST",
      `/kurulum/${encodeURIComponent(jeton)}`,
      { parola },
    ),

  /** Bir kutu icin kurulum baglantisi uretir (kimlik ister). Parola DONMEZ. */
  kutuKurulumLinki: (id: string, amac: "yeni" | "sifirlama" = "sifirlama", saat = 24) =>
    istek<{ veri: { adres: string; eposta: string; amac: string; sonKullanma: string } }>(
      "POST",
      `/kutular/${encodeURIComponent(id)}/kurulum-linki`,
      { amac, saat },
    ),

  /** Kutunun bekleyen (henuz kullanilmamis) baglantisi var mi */
  kutuKurulumLinkiVarMi: (id: string) =>
    istek<{ veri: { bekleyen: boolean } }>(
      "GET",
      `/kutular/${encodeURIComponent(id)}/kurulum-linki`,
    ),

  /**
   * Giris. Iki adimli dogrulama aciksa jeton DEGIL, ikinci adim bileti doner;
   * oturum o zaman yazilmaz.
   */
  async giris(eposta: string, parola: string) {
    const y = await istek<{
      jeton?: string;
      bitis?: string;
      kullanici?: Kullanici;
      ikiAdimGerekli?: boolean;
      bilet?: string;
      mesaj?: string;
    }>("POST", "/oturum", { eposta, parola });
    if (y.jeton && y.kullanici) oturum.yaz(y.jeton, y.kullanici);
    return y;
  },
  async ikiAdimGiris(bilet: string, kod: string) {
    const y = await istek<{
      jeton: string;
      bitis: string;
      yedekKullanildi: boolean;
      kullanici: Kullanici;
    }>("POST", "/oturum/iki-adim", { bilet, kod });
    oturum.yaz(y.jeton, y.kullanici);
    return y;
  },
  async cikis() {
    try {
      await istek("DELETE", "/oturum");
    } finally {
      oturum.temizle();
    }
  },
  saglik: () =>
    istek<{ durum: string; surum: string; ortam: string; ozellikler?: { ticari: boolean } }>(
      "GET",
      "/saglik",
    ),

  alanadlari: () => istek<{ veri: AlanAdi[]; adet: number }>("GET", "/alanadlari"),
  alanDns: (id: string) => istek<AlanDnsDurum>("GET", `/alanadlari/${id}/dns`),
  alanDogrula: (id: string) =>
    istek<AlanDnsDurum & { mesaj: string; etkilenenKutu: string[] }>("POST", `/alanadlari/${id}/dogrula`, {}),
  alanDnsUygula: (id: string) =>
    istek<{ bolge: string; sonuc: DnsYazmaSonuc[]; mesaj: string }>("POST", `/alanadlari/${id}/dns/uygula`, {}),
  alanAc: (g: { ad: string; kiraciId?: string }) =>
    istek<{
      veri: AlanAdi;
      dnsBizde: boolean;
      hazir: boolean;
      mesaj: string;
      kayitlar: DnsKayit[];
    }>("POST", "/alanadlari", g),
  // ---- dagitim listeleri
  listeler: () =>
    istek<{
      veri: Array<{ id: string; ad: string; eposta: string; aciklama: string | null; uyeSayisi: number }>;
      adet: number;
    }>("GET", "/listeler"),
  liste: (id: string) =>
    istek<{
      veri: {
        id: string; ad: string; eposta: string; aciklama: string | null;
        uyeler: Array<{ hesapId: string; eposta: string | null }>;
      };
    }>("GET", `/listeler/${encodeURIComponent(id)}`),
  listeAc: (g: { ad: string; alanAdiId: string; aciklama?: string }, anahtar?: string) =>
    istek<{ veri: { id: string; ad: string; eposta: string } }>("POST", "/listeler", g, anahtar),
  listeGuncelle: (id: string, aciklama: string) =>
    istek<{ veri: unknown }>("PATCH", `/listeler/${encodeURIComponent(id)}`, { aciklama }),
  listeSil: (id: string) =>
    istek<{ silindi: boolean; mesaj: string }>("DELETE", `/listeler/${encodeURIComponent(id)}`),
  listeUyeEkle: (id: string, hesapId: string) =>
    istek<{ mesaj: string; uyeSayisi: number }>("POST", `/listeler/${encodeURIComponent(id)}/uye`, { hesapId }),
  listeUyeCikar: (id: string, hesapId: string) =>
    istek<{ mesaj: string; uyeSayisi: number }>(
      "DELETE",
      `/listeler/${encodeURIComponent(id)}/uye/${encodeURIComponent(hesapId)}`,
    ),

  kutular: () => istek<{ veri: Kutu[]; adet: number }>("GET", "/kutular"),
  kutuAc: (g: { ad: string; alanAdiId: string; aciklama?: string }, anahtar?: string) =>
    istek<{ veri: Kutu; parola: string }>("POST", "/kutular", g, anahtar),
  kutuParola: (id: string) => istek<{ parola: string }>("POST", `/kutular/${id}/parola`, {}),
  kutu: (id: string) => istek<{ veri: KutuAyrinti }>("GET", `/kutular/${id}`),
  kutuGuncelle: (id: string, g: { aciklama?: string; kotaMb?: number }) =>
    istek<{ veri: Kutu }>("PATCH", `/kutular/${id}`, g),
  kutuAskiyaAl: (id: string, sebep: string) =>
    istek<{ durum: string; mesaj: string }>("POST", `/kutular/${id}/askiya-al`, { sebep }),
  kutuAktifEt: (id: string) => istek<{ durum: string; mesaj: string }>("POST", `/kutular/${id}/aktif-et`, {}),
  kutuSil: (id: string) =>
    istek<{ durum: string; kaliciSilme: string; mesaj: string }>("DELETE", `/kutular/${id}`, {}),
  kutuGeriAl: (id: string) => istek<{ durum: string; mesaj: string }>("POST", `/kutular/${id}/geri-al`, {}),
  takmaAdEkle: (id: string, ad: string) =>
    istek<{ mesaj: string }>("POST", `/kutular/${id}/takma-ad`, { ad }),
  takmaAdSil: (id: string, ad: string) =>
    istek<{ mesaj: string }>("DELETE", `/kutular/${id}/takma-ad/${encodeURIComponent(ad)}`),
  silmeKuyrugu: () => istek<{ veri: SilmeKaydi[] }>("GET", "/silme-kuyrugu"),
  silmeUygula: () => istek<{ veri: unknown }>("POST", "/silme-kuyrugu/uygula", {}),

  musteriler: () => istek<{ veri: Musteri[] }>("GET", "/musteriler"),
  musteri: (id: string) =>
    istek<{ veri: Musteri; alanlar: string[]; paket: Paket; kutuSayisi: number; kilitliKutu: number }>(
      "GET",
      `/musteriler/${id}`,
    ),
  musteriAc: (g: Record<string, string>, anahtar?: string) =>
    istek<{ veri: Musteri }>("POST", "/musteriler", g, anahtar),
  musteriGuncelle: (id: string, g: Record<string, string>) =>
    istek<{ veri: Musteri }>("PATCH", `/musteriler/${id}`, g),
  musteriAskiyaAl: (id: string, sebep: string) =>
    istek<{ veri: Musteri; etkilenen: string[] }>("POST", `/musteriler/${id}/askiya-al`, { sebep }),
  musteriAktifEt: (id: string) =>
    istek<{ veri: Musteri; etkilenen: string[] }>("POST", `/musteriler/${id}/aktif-et`, {}),
  musteriLimit: (id: string) => istek<{ veri: LimitDurum }>("GET", `/musteriler/${id}/limitler`),

  paketler: () => istek<{ veri: Record<string, Paket> }>("GET", "/paketler"),
  limitDenetle: () => istek<{ veri: KiraciRapor[] }>("POST", "/limitler/denetle", {}),
  kilitliler: () => istek<{ veri: KilitliKutu[] }>("GET", "/limitler/kilitliler"),

  kullanim: (gun = 30) => istek<{ veri: KullanimGun[] }>("GET", `/kullanim?gun=${gun}`),
  kutum: () => istek<KutumBilgi>("GET", "/kutum"),
  kutumParola: (eski: string, yeni: string) =>
    istek<{ mesaj: string }>("POST", "/kutum/parola", { eski, yeni }),
  kutumWebmail: () => istek<{ url: string; bitis: string; mesaj: string }>("POST", "/kutum/webmail", {}),

  pano: () => istek<{ veri: PanoOperator | PanoMusteri }>("GET", "/pano"),
  guvenlik: () => istek<GuvenlikOzet>("GET", "/guvenlik"),
  guvenlikTara: () =>
    istek<{ veri: { incelenen: number; olay: number; kilitlenen: number; bildirilen: number } }>(
      "POST",
      "/guvenlik/tara",
      {},
    ),
  guvenlikEsikler: (g: Record<string, number>) =>
    istek<{ veri: Record<string, number> }>("PATCH", "/guvenlik/esikler", g),
  bildirimDene: () => istek<{ gonderildi: boolean }>("POST", "/guvenlik/bildirim-dene", {}),
  /** Telegram botunu etkinleştir. Jeton yalnızca gider, hiç geri gelmez. */
  telegramKur: (jeton: string) =>
    istek<{ veri: { botAdi: string; sohbet: string; sohbetTuru: string; sohbetAdi: string } }>(
      "POST",
      "/guvenlik/telegram",
      { jeton },
    ),

  alarmlar: () => istek<{ veri: Alarm[] }>("GET", "/alarmlar"),
  mutabakat: () => istek<{ veri: MutabakatSonuc }>("GET", "/mutabakat"),
  mutabakatOnar: () =>
    istek<{ veri: Array<{ tur: string; ad: string; yapilan: string; basarili: boolean }>; sonrakiDurum: MutabakatSonuc }>(
      "POST",
      "/mutabakat/onar",
      {},
    ),

  // ---- ticari
  fiyatlar: () => istek<{ veri: Fiyat[]; kdvOran: number }>("GET", "/fiyatlar"),
  fiyatYaz: (paket: string, aylikKurus: number, yillikKurus: number) =>
    istek<{ veri: Fiyat }>("PATCH", `/fiyatlar/${encodeURIComponent(paket)}`, { aylikKurus, yillikKurus }),
  abonelikler: () =>
    istek<{
      veri: AbonelikSatir[];
      ozet: { toplam: number; deneme: number; aktif: number; gecikmis: number; acikBakiye: number };
    }>("GET", "/abonelikler"),
  abonelik: (kiraciId?: string) =>
    istek<{ veri: Abonelik; fiyat: Fiyat | null; donemTutari: number; acikBakiye: number }>(
      "GET",
      "/abonelik" + (kiraciId ? `?kiraciId=${encodeURIComponent(kiraciId)}` : ""),
    ),
  abonelikGuncelle: (kiraciId: string, g: Partial<Abonelik>) =>
    istek<{ veri: Abonelik }>("PATCH", `/musteriler/${kiraciId}/abonelik`, g),
  faturalar: (kiraciId?: string) =>
    istek<{
      veri: Fatura[];
      ozet: { adet: number; bekleyen: number; geciken: number; acikBakiye: number };
    }>("GET", "/faturalar" + (kiraciId ? `?kiraciId=${encodeURIComponent(kiraciId)}` : "")),
  fatura: (id: number) =>
    istek<{
      veri: Fatura;
      satirlar: FaturaSatir[];
      musteriAdi: string;
      odeme: { unvan: string; banka: string; iban: string; aciklama?: string } | null;
    }>("GET", `/faturalar/${id}`),
  faturaElle: (g: {
    kiraciId: string;
    satirlar: Array<{ aciklama: string; adet?: number; birimKurus: number }>;
    vadeGun?: number;
  }) => istek<{ veri: Fatura }>("POST", "/faturalar", g),
  faturaOdendi: (id: number, yontem: string, not?: string) =>
    istek<{ veri: Fatura; acilan: string[]; mesaj: string }>("POST", `/faturalar/${id}/odendi`, { yontem, not }),
  faturaIptal: (id: number, not?: string) =>
    istek<{ veri: Fatura }>("POST", `/faturalar/${id}/iptal`, { not }),
  faturaDongu: () => istek<{ veri: FaturaDonguSonuc }>("POST", "/fatura/dongu", {}),
  faturaKes: (kiraciId: string) =>
    istek<{ veri: { no: string; tutar: number }; mesaj: string }>("POST", `/musteriler/${kiraciId}/fatura-kes`, {}),
  faturaPdf: (id: number, no: string) => belgeIndir(`/faturalar/${id}/pdf`, `${no}.pdf`),
  yolHaritasiPdf: (alanId: string, alanAdi: string) =>
    belgeIndir(`/alanadlari/${alanId}/yol-haritasi.pdf`, `${alanAdi}-yol-haritasi.pdf`),

  // ---- istemci kurulumu
  istemciAyarlari: (eposta?: string) =>
    istek<{ veri: IstemciAyar }>(
      "GET",
      "/istemci/ayarlar" + (eposta ? `?eposta=${encodeURIComponent(eposta)}` : ""),
    ),
  appleProfil: (eposta: string) =>
    belgeIndir(
      `/istemci/apple-profil?eposta=${encodeURIComponent(eposta)}`,
      `${eposta.split("@")[1] ?? "eposta"}-eposta.mobileconfig`,
    ),

  // ---- iki adimli dogrulama
  ikiAdimDurum: () =>
    istek<{ veri: { etkin: boolean; kurulumBekliyor: boolean; kalanYedekKod: number } }>("GET", "/iki-adim"),
  ikiAdimBaslat: () => istek<{ veri: { sir: string; uri: string } }>("POST", "/iki-adim/baslat", {}),
  ikiAdimOnayla: (kod: string) =>
    istek<{ veri: { yedekKodlar: string[] }; mesaj: string }>("POST", "/iki-adim/onayla", { kod }),
  ikiAdimKapat: (kod: string) => istek<{ durum: string }>("POST", "/iki-adim/kapat", { kod }),
  ikiAdimYedekYenile: (kod: string) =>
    istek<{ veri: { yedekKodlar: string[] }; mesaj: string }>("POST", "/iki-adim/yedek-yenile", { kod }),
  guvenlikRapor: (ay?: string) =>
    istek<{ veri: Record<string, any>; metin: string }>(
      "GET",
      "/guvenlik/rapor" + (ay ? `?ay=${encodeURIComponent(ay)}` : ""),
    ),
  guvenlikRaporGonder: (ay?: string) =>
    istek<{ veri: { donem: string; dikkat: number }; gonderim: Record<string, unknown> }>(
      "POST",
      "/guvenlik/rapor/gonder",
      { ay },
    ),
  esikKiraci: (kiraciId: string) =>
    istek<{ veri: { etkin: Record<string, number>; genel: Record<string, number>; ozellestirilen: Record<string, number> } }>(
      "GET",
      `/guvenlik/esikler/${kiraciId}`,
    ),
  esikKiraciYaz: (kiraciId: string, g: Record<string, number>) =>
    istek<{ veri: unknown }>("PATCH", `/guvenlik/esikler/${kiraciId}`, g),

  // ---- tasima
  tasimaSaglayicilar: () =>
    istek<{ veri: Array<{ anahtar: string; ad: string; sunucu: string; port: number; not?: string }> }>(
      "GET",
      "/tasima/saglayicilar",
    ),
  tasimaOnizle: (g: Record<string, unknown>) =>
    istek<{ veri: { klasorler: Array<{ ad: string; ileti: number; hedef: string }>; toplamIleti: number }; mesaj: string }>(
      "POST",
      "/tasima/onizle",
      g,
    ),
  tasimaBaslat: (g: Record<string, unknown>) =>
    istek<{ veri: Record<string, unknown>; mesaj: string }>("POST", "/tasima", g),
  tasimalar: (kiraciId?: string) =>
    istek<{ veri: any[]; calisan: number }>(
      "GET",
      "/tasima" + (kiraciId ? `?kiraciId=${encodeURIComponent(kiraciId)}` : ""),
    ),
  tasima: (id: number) =>
    istek<{ veri: any; ilerleme: any[]; ozet: any }>("GET", `/tasima/${id}`),
  tasimaIptal: (id: number) => istek<{ durum: string; mesaj: string }>("POST", `/tasima/${id}/iptal`, {}),

  // ---- DMARC toplu raporlari
  dmarc: (gun = 7) =>
    istek<{ veri: { gun: number; alanlar: DmarcOzet[] } }>("GET", `/dmarc?gun=${gun}`),
  dmarcCek: () =>
    istek<{ veri: { bakilan: number; yeniRapor: number; yeniKayit: number; hata: string | null } }>(
      "POST",
      "/dmarc/cek",
      {},
    ),

  // ---- herkese acik durum
  acikDurum: () =>
    istek<{
      veri: {
        durum: "calisiyor" | "kismi" | "sorunlu";
        zaman: string;
        bilesenler: Array<{ ad: string; durum: string; aciklama: string }>;
      };
    }>("GET", "/durum"),

  // ---- kota
  kotaDurum: () => istek<{ veri: KotaDurum[]; not: string }>("GET", "/kota"),
  kotaDenetle: () =>
    istek<{
      veri: {
        incelenen: number;
        uyarilan: Array<{ kiraci: string; ad: string; esik: number; oran: number }>;
        alarmVerilen: string[];
        bildirilen: number;
      };
    }>("POST", "/kota/denetle", {}),
  alanDurdur: (id: string, sebep: string) =>
    istek<{ mesaj: string; etkilenenKutu: string[] }>("POST", `/alanadlari/${id}/durdur`, { sebep }),
  alanBaslat: (id: string) =>
    istek<{ mesaj: string; etkilenenKutu: string[] }>("POST", `/alanadlari/${id}/baslat`, {}),
  alanSil: (id: string) => istek<{ silindi: string; mesaj: string }>("DELETE", `/alanadlari/${id}`),
  denetim: () => istek<{ veri: DenetimKaydi[] }>("GET", "/denetim"),
  yaridaKalanlar: () => istek<{ veri: DenetimKaydi[] }>("GET", "/denetim/yarida-kalanlar"),
};

// --------------------------------------------------------------- bicimleme

export function mb(bayt: number): string {
  if (bayt < 1024) return bayt + " B";
  if (bayt < 1048576) return (bayt / 1024).toFixed(1) + " KB";
  if (bayt < 1073741824) return (bayt / 1048576).toFixed(1) + " MB";
  return (bayt / 1073741824).toFixed(2) + " GB";
}

export function tarih(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s.includes("T") ? s : s.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
}

export function sayi(n: number): string {
  return n.toLocaleString("tr-TR");
}
